/**
 * Générateur — Représenter une situation de proportionnalité par un tableau ou un graphique.
 * Attendu ID : 985 | Niveau : 5ème | Domaine : proportionnalite
 *
 * ═══════════════════════════════════════════════════════════════════
 * PLAN DE CARTES (version conforme à la spécification 985.json, 2026-09-21)
 * ═══════════════════════════════════════════════════════════════════
 *
 * Libellé officiel (spec.attendu) : « Représenter une situation de proportionnalité par un
 * tableau ou un graphique. »
 *
 * Le verbe est « représenter » : l'élève PRODUIT la représentation à partir d'une situation
 * décrite. `exclusions` 1 et 2 interdisent de fournir un tableau déjà construit à compléter, et
 * la restriction du programme écarte la lecture d'une représentation (objet de l'attendu 986).
 *
 * Catégorie « tableau » (niveau 1 : produire le tableau à partir de la situation décrite)
 *   - tableau_construire_trou_drag   Trous (drag) : nommer les deux grandeurs AVEC leur unité,
 *                                    puis poser les trois valeurs
 *   - tableau_entetes_qcm_unique     QCM : lequel de ces quatre tableaux représente la situation
 *   - tableau_entetes_tableau_vf     V/F : quatre affirmations sur un tableau produit
 *
 * Catégorie « graphique » (niveaux 2, 3 et 4 : produire le graphique)
 *   - graphique_couples_texte_trou   Trous (saisie) : les couples à représenter, avant placement
 *   - graphique_placer_2d            Placer les points du tableau dans un repère gradué (niveau 2)
 *   - graphique_droite_placer_2d     Placer les points, l'origine et le prolongement (niveau 3)
 *   - graphique_conforme_qcm_unique  QCM : quelle représentation est correcte
 *   - graphique_lecture_tableau_vf   V/F : ce que la représentation doit vérifier
 *   - graphique_graduation_qcm_unique QCM (niveau 4) : quel couple de graduations convient
 *   - graphique_graduation_texte     Saisie (niveau 4) : le pas de l'axe des ordonnées
 *
 * ── Spécification appliquée ──
 * Bornes :
 *   - representation_a_produire : tableau et graphique, les deux servis.
 *   - contexte_de_la_situation : les cinq contextes de la spec (prix à la quantité, distance à
 *     vitesse constante, ingrédient par personne, consommation, longueur sur un plan). Chaque
 *     grandeur est nommée et porte son unité (`exclusions` 4).
 *   - nombre_de_couples_a_representer : 3 (dans [3, 6]).
 *   - coefficient_de_la_situation : [0,2 ; 25], choisi pour que toutes les ordonnées tombent sur
 *     un nœud du quadrillage (`points_de_vigilance` 2).
 *   - valeur_de_la_grandeur_en_abscisse : [1, 12], entière ou au demi, distinctes et croissantes,
 *     toujours sur une graduation. La valeur 0 n'est jamais donnée (`exclusions` 6).
 *   - pas_de_graduation_de_l_axe_des_abscisses : 0,5, 1, 2 ou 5 ; la plus grande abscisse occupe
 *     entre six et douze carreaux.
 *   - pas_de_graduation_de_l_axe_des_ordonnees : 1, 2, 5, 10, 20, 50 ou 100 ; la plus grande
 *     ordonnée occupe entre six et douze carreaux et tombe sur un nœud.
 *   - orientation_du_tableau : lignes et colonnes, les deux servies.
 *   - valeur_maximale_representable_sur_l_axe : déduite, contrôlée à la génération, au moins un
 *     carreau de marge au-delà du plus grand point.
 *   Écart minimal entre deux points consécutifs : un carreau et demi sur l'axe des abscisses
 *   (`points_de_vigilance` 3).
 * Exclusions respectées : aucun tableau fourni déjà construit, aucune représentation préremplie
 *   (`points_de_vigilance` 9), aucun point entre deux nœuds, aucune grandeur anonyme, aucune
 *   équation de droite ni fonction linéaire nommée, aucun couple de valeur nulle donné.
 *
 * Erreurs visées → mots-clés (codes des distracteurs et des cartes qui les provoquent) :
 *   1. droite qui ne passe pas par l'origine, ou absente   → `droite_sans_origine`
 *   2. points reliés par une ligne brisée                  → `ligne_brisee`
 *   3. abscisse et ordonnée inversées                      → `axes_inverses`
 *   4. échelle qui fait sortir le plus grand point, ou qui écrase tout dans un coin
 *                                                           → `echelle_inadaptee`
 *   5. même graduation sur les deux axes                   → `meme_graduation_sur_les_deux_axes`
 *   6. tableau sans en-têtes ni unités                     → `tableau_sans_unites`
 *   7. point placé en lisant la mauvaise graduation        → `graduation_mal_lue`
 *
 * Graduation → cartes :
 *   niveau 1 → tableau_construire_trou_drag, tableau_entetes_qcm_unique,
 *              tableau_entetes_tableau_vf
 *   niveau 2 → graphique_couples_texte_trou, graphique_placer_2d, graphique_conforme_qcm_unique
 *   niveau 3 → graphique_droite_placer_2d, graphique_lecture_tableau_vf
 *   niveau 4 → graphique_graduation_qcm_unique, graphique_graduation_texte
 *
 * Points non tranchés (voir audit/5eme/985.md) :
 *   - Le repère VIERGE et non gradué du niveau 4 n'existe pas dans le composant : l'élève ne
 *     peut pas poser lui-même les graduations. Le choix d'échelle est donc évalué par les deux
 *     cartes « graduation », qui portent le critère explicite du point de vigilance 8, et non
 *     par une construction libre.
 *   - Les en-têtes « comparés à une liste de formulations acceptées » (type_reponse,
 *     `points_de_vigilance` 7) ne sont pas saisissables librement : un trou se compare mot à
 *     mot. Les en-têtes sont donc posés par glissement, parmi des étiquettes dont certaines
 *     sont privées d'unité — l'erreur visée 6 reste servie, la liberté de formulation non.
 *   - La validation « aucun point superflu » (`points_de_vigilance` 4) est assurée par le
 *     composant, qui n'accepte qu'un point par étiquette annoncée.
 *
 * Invariants : arithmétique entière (dixièmes de coefficient), toutes les ordonnées sur un nœud
 * vérifié à la génération, coordonnées écrites exactement comme le composant les émet (virgule
 * française), boucles bornées avec repli déterministe, jamais de throw.
 */

import {
    randomChoice, randInt, shuffle,
    basePayload, makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 985;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Mots-clés des erreurs visées (spec 985.json) ───
export const ERREURS_VISEES = {
    droite_sans_origine: 'Ne pas faire passer la droite par l\'origine, ou ne pas la tracer du tout',
    ligne_brisee: 'Relier les points par une ligne brisée au lieu d\'une droite',
    axes_inverses: 'Inverser abscisse et ordonnée',
    echelle_inadaptee: 'Choisir une échelle qui fait sortir le plus grand point de la fenêtre, ou qui écrase tous les points dans un coin',
    meme_graduation_sur_les_deux_axes: 'Employer la même graduation sur les deux axes alors que les grandeurs n\'ont ni la même unité ni le même ordre de grandeur',
    tableau_sans_unites: 'Produire un tableau sans en-têtes ni unités',
    graduation_mal_lue: 'Placer un point en lisant la mauvaise graduation, en comptant les carreaux au lieu de lire les valeurs portées sur l\'axe',
};

export const REGLES_DISTRACTEURS_QCM = { ...ERREURS_VISEES };

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

/** Écriture française d'un nombre, à l'identique de l'émission du composant de repère. */
const fmtFr = (v) => String(Math.round(v * 1000) / 1000).replace('.', ',');

const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

const PAS_X = [0.5, 1, 2, 5];
const PAS_Y = [1, 2, 5, 10, 20, 50, 100];

/**
 * Les cinq contextes de la spécification. `coeff` est en DIXIÈMES (25 = 2,5), les
 * grandeurs portent leur nom complet et leur unité, et `etiquette` donne la
 * formulation d'en-tête de tableau attendue.
 */
const CONTEXTES = [
    {
        id: 'prix',
        situation: (k) => `Des cerises sont vendues ${fmtFr(k / 10)} € le kilogramme.`,
        xNom: 'masse', xUnite: 'kg', xEtiquette: 'la masse en kilogrammes', xAxe: 'masse (kg)',
        yNom: 'prix', yUnite: '€', yEtiquette: 'le prix en euros', yAxe: 'prix (€)',
        xs: [1, 2, 3, 4, 5, 6, 8, 10], coeffs: [20, 25, 30, 40, 50, 60],
    },
    {
        id: 'distance',
        situation: (k) => `Un cycliste roule à allure constante et parcourt ${fmtFr(k / 10)} km chaque heure.`,
        xNom: 'durée', xUnite: 'h', xEtiquette: 'la durée en heures', xAxe: 'durée (h)',
        yNom: 'distance', yUnite: 'km', yEtiquette: 'la distance en kilomètres', yAxe: 'distance (km)',
        xs: [1, 2, 3, 4, 5, 6], coeffs: [120, 150, 180, 200, 240],
    },
    {
        id: 'recette',
        situation: (k) => `Une recette demande ${fmtFr(k / 10)} g de farine par personne.`,
        xNom: 'nombre de personnes', xUnite: 'personnes', xEtiquette: 'le nombre de personnes', xAxe: 'nombre de personnes',
        yNom: 'masse de farine', yUnite: 'g', yEtiquette: 'la masse de farine en grammes', yAxe: 'masse de farine (g)',
        xs: [2, 3, 4, 5, 6, 8, 10, 12], coeffs: [200, 250],
    },
    {
        id: 'consommation',
        situation: (k) => `Une tondeuse consomme ${fmtFr(k / 10)} litre${k >= 20 ? 's' : ''} de carburant par heure de travail.`,
        xNom: 'durée', xUnite: 'h', xEtiquette: 'la durée en heures', xAxe: 'durée (h)',
        yNom: 'volume de carburant', yUnite: 'L', yEtiquette: 'le volume en litres', yAxe: 'volume (L)',
        xs: [1, 2, 3, 4, 5, 6, 8], coeffs: [20, 25, 30, 40],
    },
    {
        id: 'plan',
        situation: (k) => `Sur un plan de jardin, 1 cm représente ${fmtFr(k / 10)} m en réalité.`,
        xNom: 'longueur sur le plan', xUnite: 'cm', xEtiquette: 'la longueur sur le plan en centimètres', xAxe: 'longueur sur le plan (cm)',
        yNom: 'longueur réelle', yUnite: 'm', yEtiquette: 'la longueur réelle en mètres', yAxe: 'longueur réelle (m)',
        xs: [1, 2, 3, 4, 5, 6, 8], coeffs: [20, 25, 30, 50],
    },
];

/**
 * Tire une situation complète : contexte, coefficient, trois abscisses, et le repère
 * qui va avec. Toutes les ordonnées tombent sur un nœud du quadrillage, le plus grand
 * point occupe entre six et douze carreaux sur chaque axe, et deux points consécutifs
 * sont séparés d'au moins un carreau et demi en abscisse.
 */
const tirerSituation = () => {
    for (let essai = 0; essai < 200; essai++) {
        const ctx = randomChoice(CONTEXTES);
        const kd = randomChoice(ctx.coeffs);
        const xs = shuffle([...ctx.xs]).slice(0, 3).sort((a, b) => a - b);
        if (new Set(xs).size !== 3) continue;
        if (xs.some((x) => (kd * x) % 10 !== 0)) continue;          // ordonnées entières
        const ys = xs.map((x) => (kd * x) / 10);
        const xStep = randomChoice(PAS_X);
        if (xs.some((x) => Math.round(x / xStep) * xStep !== x)) continue;
        if (xs[1] - xs[0] < 1.5 * xStep || xs[2] - xs[1] < 1.5 * xStep) continue;
        const xMax = Math.round((xs[2] + xStep) / xStep) * xStep;
        const nbX = Math.round(xMax / xStep);
        if (nbX < 6 || nbX > 12) continue;
        const yStep = randomChoice(PAS_Y);
        if (ys.some((y) => y % yStep !== 0)) continue;
        const yMax = Math.round((ys[2] + yStep) / yStep) * yStep;
        const nbY = Math.round(yMax / yStep);
        if (nbY < 6 || nbY > 12) continue;
        if (ys[1] - ys[0] < 1.5 * yStep || ys[2] - ys[1] < 1.5 * yStep) continue;
        return { ctx, kd, xs, ys, xStep, yStep, xMax, yMax };
    }
    const ctx = CONTEXTES[0];
    return { ctx, kd: 50, xs: [2, 4, 6], ys: [10, 20, 30], xStep: 1, yStep: 5, xMax: 7, yMax: 35 };
};

/** Payload « placer des points dans un repère » (composant Repere2DInteractiveEleve). */
const makePlacer2D = (texte, repere, points, label, params) => {
    const bonne = {};
    points.forEach((p) => { bonne[p.label] = `${fmtFr(p.x)};${fmtFr(p.y)}`; });
    const payload = basePayload('association', texte, {
        subType: 'placer_points_2d',
        repere,
        pointsToPlace: points.map((p) => p.label),
        bonne_reponse: JSON.stringify(bonne),
    }, opts(label));
    payload._meta.params = params;
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 1 — PRODUIRE LE TABLEAU (niveau 1)
// ═══════════════════════════════════════════════════════════

const genTableauConstruireTrou = () => {
    const s = tirerSituation();
    const orientation = randInt(0, 1) === 0 ? 'lignes' : 'colonnes';
    const mots = [
        s.ctx.xEtiquette,
        s.ctx.yEtiquette,
        ...s.ys.map((y) => `${fmtFr(y)} ${s.ctx.yUnite}`),
    ];
    const distracteurs = [
        { code: 'tableau_sans_unites', valeur: s.ctx.xNom },
        { code: 'tableau_sans_unites', valeur: s.ctx.yNom },
        { code: 'graduation_mal_lue', valeur: `${fmtFr(s.ys[2] + s.yStep)} ${s.ctx.yUnite}` },
    ];
    if (distracteurs.some((d) => mots.includes(d.valeur))) {
        distracteurs[2] = { code: 'graduation_mal_lue', valeur: `${fmtFr(s.ys[2] + 2 * s.yStep)} ${s.ctx.yUnite}` };
    }
    const entete = orientation === 'lignes' ? 'ligne' : 'colonne';
    const phrase = `La première ${entete} du tableau porte {{${s.ctx.xEtiquette}}}\n`
        + `La seconde ${entete} porte {{${s.ctx.yEtiquette}}}\n`
        + s.xs.map((x, i) => `Pour ${fmtFr(x)} ${s.ctx.xUnite}, le tableau indique {{${fmtFr(s.ys[i])} ${s.ctx.yUnite}}}`).join('\n');
    const payload = makeTexteTrou(
        phrase,
        `<p>${s.ctx.situation(s.kd)}</p>`
        + `<p>Construis le tableau de cette situation en ${orientation}, pour ${s.xs.map((x) => `${fmtFr(x)} ${s.ctx.xUnite}`).join(', ')}.</p>`,
        { ...opts('Construire le tableau d\'une situation'), mode: 'drag', distracteurs: distracteurs.map((d) => d.valeur) },
    );
    payload._meta.params = {
        niveau: 1, representation: 'tableau', contexte: s.ctx.id, orientation,
        coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys, distracteurs,
    };
    return payload;
};

const genTableauEntetesQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const s = tirerSituation();
        const orientation = randInt(0, 1) === 0 ? 'lignes' : 'colonnes';
        const valeursX = s.xs.map((x) => fmtFr(x)).join(' ; ');
        const valeursY = s.ys.map((y) => fmtFr(y)).join(' ; ');
        const bonne = `${s.ctx.xEtiquette} (${valeursX}) et ${s.ctx.yEtiquette} (${valeursY})`;
        const distracteurs = [
            { code: 'tableau_sans_unites', valeur: `première grandeur (${valeursX}) et seconde grandeur (${valeursY})` },
            { code: 'axes_inverses', valeur: `${s.ctx.xEtiquette} (${valeursY}) et ${s.ctx.yEtiquette} (${valeursX})` },
            {
                code: 'graduation_mal_lue',
                valeur: `${s.ctx.xEtiquette} (${valeursX}) et ${s.ctx.yEtiquette} (${[s.ys[0], s.ys[1], s.ys[2] + s.yStep].map((y) => fmtFr(y)).join(' ; ')})`,
            },
        ];
        const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
        if (new Set(valeurs).size !== 4) continue;
        const payload = makeQcm(
            valeurs, bonne,
            `<p>${s.ctx.situation(s.kd)}</p>`
            + `<p>On veut représenter cette situation par un tableau en ${orientation}, pour ${s.xs.map((x) => `${fmtFr(x)} ${s.ctx.xUnite}`).join(', ')}.</p>`
            + '<p>Quel tableau représente correctement la situation ?</p>',
            opts('Choisir le tableau qui représente la situation'),
        );
        payload._meta.params = {
            niveau: 1, representation: 'tableau', contexte: s.ctx.id, orientation,
            coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys,
            reponse: bonne, distracteurs,
        };
        return payload;
    }
    const s = { ctx: CONTEXTES[0], kd: 50, xs: [2, 4, 6], ys: [10, 20, 30], xStep: 1, yStep: 5, xMax: 7, yMax: 35 };
    const bonne = 'la masse en kilogrammes (2 ; 4 ; 6) et le prix en euros (10 ; 20 ; 30)';
    const distracteurs = [
        { code: 'tableau_sans_unites', valeur: 'première grandeur (2 ; 4 ; 6) et seconde grandeur (10 ; 20 ; 30)' },
        { code: 'axes_inverses', valeur: 'la masse en kilogrammes (10 ; 20 ; 30) et le prix en euros (2 ; 4 ; 6)' },
        { code: 'graduation_mal_lue', valeur: 'la masse en kilogrammes (2 ; 4 ; 6) et le prix en euros (10 ; 20 ; 35)' },
    ];
    const payload = makeQcm([bonne, ...distracteurs.map((d) => d.valeur)], bonne,
        `<p>${s.ctx.situation(s.kd)}</p><p>Quel tableau représente correctement la situation ?</p>`,
        opts('Choisir le tableau qui représente la situation'));
    payload._meta.params = {
        niveau: 1, representation: 'tableau', contexte: s.ctx.id, orientation: 'lignes',
        coefficient: 5, couples: 3, xs: s.xs, ys: s.ys, reponse: bonne, distracteurs,
    };
    return payload;
};

const genTableauEntetesVF = () => {
    const s = tirerSituation();
    const orientation = randInt(0, 1) === 0 ? 'lignes' : 'colonnes';
    const plan = shuffle([true, true, false, false]);
    const distracteurs = [];
    const lignes = [
        plan[0]
            ? `Les en-têtes du tableau doivent porter le nom de chaque grandeur et son unité`
            : `Les en-têtes du tableau peuvent se réduire aux lettres A et B`,
        plan[1]
            ? `Pour ${fmtFr(s.xs[1])} ${s.ctx.xUnite}, le tableau indique ${fmtFr(s.ys[1])} ${s.ctx.yUnite}`
            : `Pour ${fmtFr(s.xs[1])} ${s.ctx.xUnite}, le tableau indique ${fmtFr(s.ys[1] + s.yStep)} ${s.ctx.yUnite}`,
        plan[2]
            ? `Le tableau comporte ${s.xs.length} couples de valeurs`
            : `Le tableau comporte ${s.xs.length + 2} couples de valeurs`,
        plan[3]
            ? `Pour ${fmtFr(s.xs[2])} ${s.ctx.xUnite}, le tableau indique ${fmtFr(s.ys[2])} ${s.ctx.yUnite}`
            : `Pour ${fmtFr(s.xs[2])} ${s.ctx.xUnite}, le tableau indique ${fmtFr(s.xs[2])} ${s.ctx.yUnite}`,
    ];
    if (!plan[0]) distracteurs.push({ code: 'tableau_sans_unites', valeur: 'lettres A et B' });
    if (!plan[1]) distracteurs.push({ code: 'graduation_mal_lue', valeur: fmtFr(s.ys[1] + s.yStep) });
    if (!plan[3]) distracteurs.push({ code: 'axes_inverses', valeur: fmtFr(s.xs[2]) });
    const payload = makeVraiFaux(
        lignes, plan.map((x) => (x ? 0 : 1)),
        `<p>${s.ctx.situation(s.kd)}</p>`
        + `<p>On représente cette situation par un tableau en ${orientation}, pour ${s.xs.map((x) => `${fmtFr(x)} ${s.ctx.xUnite}`).join(', ')}.</p>`
        + '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>',
        opts('Vrai/Faux sur le tableau produit'),
    );
    payload._meta.params = {
        niveau: 1, representation: 'tableau', contexte: s.ctx.id, orientation,
        coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys, distracteurs,
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 2 — PRODUIRE LE GRAPHIQUE (niveaux 2, 3 et 4)
// ═══════════════════════════════════════════════════════════

const genGraphiqueCouplesTrou = () => {
    const s = tirerSituation();
    const phrase = s.xs.map((x, i) => `Pour ${fmtFr(x)} ${s.ctx.xUnite}, le point à placer a pour ordonnée {{${fmtFr(s.ys[i])}}}`).join('\n');
    const payload = makeTexteTrou(
        phrase,
        `<p>${s.ctx.situation(s.kd)}</p>`
        + `<p>On veut représenter cette situation dans un repère, avec ${s.ctx.xAxe} en abscisse et ${s.ctx.yAxe} en ordonnée.</p>`
        + '<p>Donne l\'ordonnée de chaque point à placer. Écris seulement le nombre.</p>',
        { ...opts('Les couples à représenter'), mode: 'saisie' },
    );
    payload._meta.params = {
        niveau: 2, representation: 'graphique', contexte: s.ctx.id,
        coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys,
        erreursVisees: ['axes_inverses'],
    };
    return payload;
};

const genGraphiquePlacer2D = () => {
    const s = tirerSituation();
    const labels = ['A', 'B', 'C'];
    const points = s.xs.map((x, i) => ({ label: labels[i], x, y: s.ys[i] }));
    const tableau = tableEnonce(
        ligneTable('Point', labels),
        ligneTable(s.ctx.xAxe, s.xs.map((x) => fmtFr(x))),
        ligneTable(s.ctx.yAxe, s.ys.map((y) => fmtFr(y))),
    );
    return makePlacer2D(
        `<p>${s.ctx.situation(s.kd)}</p>\n${tableau}\n`
        + '<p>Représente cette situation : place les points A, B et C dans le repère. '
        + 'Chaque point tombe sur un nœud du quadrillage.</p>',
        {
            xMax: s.xMax, yMax: s.yMax, xStep: s.xStep, yStep: s.yStep,
            xLabel: s.ctx.xAxe, yLabel: s.ctx.yAxe, grille: true,
        },
        points, 'Représenter une proportionnalité (placer les points)',
        {
            niveau: 2, representation: 'graphique', contexte: s.ctx.id,
            coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys,
            xStep: s.xStep, yStep: s.yStep, xMax: s.xMax, yMax: s.yMax,
            erreursVisees: ['graduation_mal_lue'],
        },
    );
};

const genGraphiqueDroitePlacer2D = () => {
    for (let essai = 0; essai < 60; essai++) {
        const s = tirerSituation();
        // Le prolongement : un point de la droite au-delà du dernier, dans la fenêtre.
        const xP = s.xs[2] + s.xStep;
        const yP = (s.kd * xP) / 10;
        if (!Number.isInteger(yP) || yP % s.yStep !== 0) continue;
        if (xP > s.xMax || yP > s.yMax) continue;
        const labels = ['O', 'A', 'B', 'C', 'P'];
        const points = [
            { label: 'O', x: 0, y: 0 },
            ...s.xs.map((x, i) => ({ label: labels[i + 1], x, y: s.ys[i] })),
            { label: 'P', x: xP, y: yP },
        ];
        const tableau = tableEnonce(
            ligneTable('Point', ['A', 'B', 'C']),
            ligneTable(s.ctx.xAxe, s.xs.map((x) => fmtFr(x))),
            ligneTable(s.ctx.yAxe, s.ys.map((y) => fmtFr(y))),
        );
        return makePlacer2D(
            `<p>${s.ctx.situation(s.kd)}</p>\n${tableau}\n`
            + '<p>Place les points A, B et C, puis les deux points par lesquels passe la droite de cette situation : '
            + `le point O de la droite qui est sur l'origine du repère, et le point P de la droite d'abscisse ${fmtFr(xP)} ${s.ctx.xUnite}, qui la prolonge.</p>`,
            {
                xMax: s.xMax, yMax: s.yMax, xStep: s.xStep, yStep: s.yStep,
                xLabel: s.ctx.xAxe, yLabel: s.ctx.yAxe, grille: true,
            },
            points, 'Placer les points, l\'origine et le prolongement',
            {
                niveau: 3, representation: 'graphique', contexte: s.ctx.id,
                coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys,
                xStep: s.xStep, yStep: s.yStep, xMax: s.xMax, yMax: s.yMax,
                erreursVisees: ['droite_sans_origine'],
            },
        );
    }
    const s = { ctx: CONTEXTES[0], kd: 50, xs: [2, 4, 6], ys: [10, 20, 30], xStep: 1, yStep: 5, xMax: 8, yMax: 40 };
    const points = [
        { label: 'O', x: 0, y: 0 },
        { label: 'A', x: 2, y: 10 }, { label: 'B', x: 4, y: 20 }, { label: 'C', x: 6, y: 30 },
        { label: 'P', x: 7, y: 35 },
    ];
    return makePlacer2D(
        `<p>${s.ctx.situation(s.kd)}</p><p>Place les points A, B et C, puis le point O sur l'origine et le point P d'abscisse 7 kg.</p>`,
        { xMax: 8, yMax: 40, xStep: 1, yStep: 5, xLabel: s.ctx.xAxe, yLabel: s.ctx.yAxe, grille: true },
        points, 'Placer les points, l\'origine et le prolongement',
        {
            niveau: 3, representation: 'graphique', contexte: s.ctx.id, coefficient: 5,
            couples: 3, xs: s.xs, ys: s.ys, xStep: 1, yStep: 5, xMax: 8, yMax: 40,
            erreursVisees: ['droite_sans_origine'],
        },
    );
};

const genGraphiqueConformeQcm = () => {
    const s = tirerSituation();
    const bonne = `Les trois points sont alignés, la droite qui les porte passe par l'origine du repère et elle est prolongée jusqu'au bord.`;
    const distracteurs = [
        { code: 'droite_sans_origine', valeur: 'Les trois points sont alignés et la droite qui les porte s\'arrête au premier point, sans passer par l\'origine.' },
        { code: 'ligne_brisee', valeur: 'Les trois points sont reliés l\'un à l\'autre par des segments, ce qui donne une ligne brisée.' },
        { code: 'axes_inverses', valeur: `Les points sont placés avec ${s.ctx.yAxe} en abscisse et ${s.ctx.xAxe} en ordonnée.` },
    ];
    const payload = makeQcm(
        [bonne, ...distracteurs.map((d) => d.valeur)], bonne,
        `<p>${s.ctx.situation(s.kd)}</p>`
        + `<p>On représente cette situation dans un repère, avec ${s.ctx.xAxe} en abscisse et ${s.ctx.yAxe} en ordonnée, pour les couples ${s.xs.map((x, i) => `${fmtFr(x)} ${s.ctx.xUnite} pour ${fmtFr(s.ys[i])} ${s.ctx.yUnite}`).join(' ; ')}.</p>`
        + '<p>Quelle représentation graphique est correcte ?</p>',
        opts('Choisir la représentation graphique correcte'),
    );
    payload._meta.params = {
        niveau: 2, representation: 'graphique', contexte: s.ctx.id,
        coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys,
        reponse: bonne, distracteurs,
    };
    return payload;
};

const genGraphiqueLectureVF = () => {
    const s = tirerSituation();
    const plan = shuffle([true, true, false, false]);
    const distracteurs = [];
    const lignes = [
        plan[0]
            ? 'La droite de cette représentation passe par l\'origine du repère'
            : 'La droite de cette représentation ne passe pas par l\'origine du repère',
        plan[1]
            ? `Le point d'abscisse ${fmtFr(s.xs[0])} ${s.ctx.xUnite} a pour ordonnée ${fmtFr(s.ys[0])} ${s.ctx.yUnite}`
            : `Le point d'abscisse ${fmtFr(s.xs[0])} ${s.ctx.xUnite} a pour ordonnée ${fmtFr(s.ys[0] + s.yStep)} ${s.ctx.yUnite}`,
        plan[2]
            ? 'Les points de cette représentation sont alignés'
            : 'Les points de cette représentation forment une ligne brisée',
        plan[3]
            ? `L'axe des abscisses porte ${s.ctx.xAxe} et l'axe des ordonnées porte ${s.ctx.yAxe}`
            : `L'axe des abscisses porte ${s.ctx.yAxe} et l'axe des ordonnées porte ${s.ctx.xAxe}`,
    ];
    if (!plan[0]) distracteurs.push({ code: 'droite_sans_origine', valeur: 'ne passe pas par l\'origine' });
    if (!plan[1]) distracteurs.push({ code: 'graduation_mal_lue', valeur: fmtFr(s.ys[0] + s.yStep) });
    if (!plan[2]) distracteurs.push({ code: 'ligne_brisee', valeur: 'ligne brisée' });
    if (!plan[3]) distracteurs.push({ code: 'axes_inverses', valeur: s.ctx.yAxe });
    const payload = makeVraiFaux(
        lignes, plan.map((x) => (x ? 0 : 1)),
        `<p>${s.ctx.situation(s.kd)}</p>`
        + `<p>On représente cette situation dans un repère, avec ${s.ctx.xAxe} en abscisse et ${s.ctx.yAxe} en ordonnée.</p>`
        + '<p>Pour chaque affirmation sur cette représentation, indique si elle est vraie ou fausse.</p>',
        opts('Vrai/Faux sur la représentation graphique'),
    );
    payload._meta.params = {
        niveau: 3, representation: 'graphique', contexte: s.ctx.id,
        coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys, distracteurs,
    };
    return payload;
};

/**
 * Niveau 4 : le choix des graduations. La spec demande un repère vierge, que le
 * composant ne sait pas produire ; le choix est donc évalué par le critère explicite
 * du point de vigilance 8 — tous les points tiennent, et le plus grand occupe entre
 * six et douze carreaux.
 */
const genGraphiqueGraduationQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const s = tirerSituation();
        const trop = PAS_Y.find((p) => p > s.yStep && s.ys[2] / p < 3);
        const pasAssez = PAS_Y.slice().reverse().find((p) => p < s.yStep && s.ys[2] / p > 20);
        if (!trop || !pasAssez) continue;
        const bonne = `${fmtFr(s.xStep)} ${s.ctx.xUnite} par carreau en abscisse et ${fmtFr(s.yStep)} ${s.ctx.yUnite} par carreau en ordonnée`;
        const distracteurs = [
            { code: 'echelle_inadaptee', valeur: `${fmtFr(s.xStep)} ${s.ctx.xUnite} par carreau en abscisse et ${fmtFr(trop)} ${s.ctx.yUnite} par carreau en ordonnée` },
            { code: 'echelle_inadaptee', valeur: `${fmtFr(s.xStep)} ${s.ctx.xUnite} par carreau en abscisse et ${fmtFr(pasAssez)} ${s.ctx.yUnite} par carreau en ordonnée` },
            { code: 'meme_graduation_sur_les_deux_axes', valeur: `1 unité par carreau sur les deux axes, la même graduation en abscisse et en ordonnée` },
        ];
        const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
        if (new Set(valeurs).size !== 4) continue;
        const payload = makeQcm(
            valeurs, bonne,
            `<p>${s.ctx.situation(s.kd)}</p>`
            + `<p>On veut représenter les couples suivants dans un repère vierge, sur une feuille de douze carreaux de côté : `
            + `${s.xs.map((x, i) => `${fmtFr(x)} ${s.ctx.xUnite} pour ${fmtFr(s.ys[i])} ${s.ctx.yUnite}`).join(' ; ')}.</p>`
            + '<p>Quel couple de graduations convient, pour que tous les points tiennent dans la feuille sans être écrasés dans un coin ?</p>',
            opts('Choisir les graduations des deux axes'),
        );
        payload._meta.params = {
            niveau: 4, representation: 'graphique', contexte: s.ctx.id,
            coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys,
            xStep: s.xStep, yStep: s.yStep, reponse: bonne, distracteurs,
        };
        return payload;
    }
    const bonne = '1 kg par carreau en abscisse et 5 € par carreau en ordonnée';
    const distracteurs = [
        { code: 'echelle_inadaptee', valeur: '1 kg par carreau en abscisse et 50 € par carreau en ordonnée' },
        { code: 'echelle_inadaptee', valeur: '1 kg par carreau en abscisse et 1 € par carreau en ordonnée' },
        { code: 'meme_graduation_sur_les_deux_axes', valeur: '1 unité par carreau sur les deux axes, la même graduation en abscisse et en ordonnée' },
    ];
    const payload = makeQcm([bonne, ...distracteurs.map((d) => d.valeur)], bonne,
        '<p>Des cerises sont vendues 5 € le kilogramme.</p>'
        + '<p>On veut représenter les couples suivants dans un repère vierge, sur une feuille de douze carreaux de côté : 2 kg pour 10 € ; 4 kg pour 20 € ; 6 kg pour 30 €.</p>'
        + '<p>Quel couple de graduations convient, pour que tous les points tiennent dans la feuille sans être écrasés dans un coin ?</p>',
        opts('Choisir les graduations des deux axes'));
    payload._meta.params = {
        niveau: 4, representation: 'graphique', contexte: 'prix', coefficient: 5,
        couples: 3, xs: [2, 4, 6], ys: [10, 20, 30], xStep: 1, yStep: 5,
        reponse: bonne, distracteurs,
    };
    return payload;
};

const genGraphiqueGraduationTexte = () => {
    for (let essai = 0; essai < 80; essai++) {
        const s = tirerSituation();
        // Le pas est unique quand un seul pas de la liste place la plus grande
        // ordonnée entre six et douze carreaux (point de vigilance 8).
        const admissibles = PAS_Y.filter((p) => s.ys[2] % p === 0 && s.ys[2] / p >= 6 && s.ys[2] / p <= 12);
        if (admissibles.length !== 1) continue;
        const payload = makeSaisie(
            fmtFr(admissibles[0]),
            `<p>${s.ctx.situation(s.kd)}</p>`
            + `<p>On veut représenter ${s.ctx.yAxe} en ordonnée, jusqu'à ${fmtFr(s.ys[2])} ${s.ctx.yUnite}, sur un axe de douze carreaux.</p>`
            + '<p>Quelle graduation choisis-tu pour un carreau, pour que la plus grande valeur occupe entre six et douze carreaux et tombe sur un nœud ? Écris seulement le nombre.</p>',
            { ...opts('Choisir la graduation de l\'axe des ordonnées'), suffixe: `${s.ctx.yUnite} par carreau` },
        );
        payload._meta.params = {
            niveau: 4, representation: 'graphique', contexte: s.ctx.id,
            coefficient: s.kd / 10, couples: s.xs.length, xs: s.xs, ys: s.ys,
            xStep: s.xStep, yStep: admissibles[0], reponse: fmtFr(admissibles[0]),
            erreurVisee: 'echelle_inadaptee',
        };
        return payload;
    }
    const payload = makeSaisie(
        '5',
        '<p>Des cerises sont vendues 5 € le kilogramme.</p>'
        + '<p>On veut représenter le prix (€) en ordonnée, jusqu\'à 30 €, sur un axe de douze carreaux.</p>'
        + '<p>Quelle graduation choisis-tu pour un carreau, pour que la plus grande valeur occupe entre six et douze carreaux et tombe sur un nœud ? Écris seulement le nombre.</p>',
        { ...opts('Choisir la graduation de l\'axe des ordonnées'), suffixe: '€ par carreau' },
    );
    payload._meta.params = {
        niveau: 4, representation: 'graphique', contexte: 'prix', coefficient: 5,
        couples: 3, xs: [2, 4, 6], ys: [10, 20, 30], xStep: 1, yStep: 5, reponse: '5',
        erreurVisee: 'echelle_inadaptee',
    };
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    tableau_construire_trou_drag: genTableauConstruireTrou,
    tableau_entetes_qcm_unique: genTableauEntetesQcm,
    tableau_entetes_tableau_vf: genTableauEntetesVF,

    graphique_couples_texte_trou: genGraphiqueCouplesTrou,
    graphique_placer_2d: genGraphiquePlacer2D,
    graphique_droite_placer_2d: genGraphiqueDroitePlacer2D,
    graphique_conforme_qcm_unique: genGraphiqueConformeQcm,
    graphique_lecture_tableau_vf: genGraphiqueLectureVF,
    graphique_graduation_qcm_unique: genGraphiqueGraduationQcm,
    graphique_graduation_texte: genGraphiqueGraduationTexte,
};

export const CATEGORIES = [
    {
        id: 'tableau',
        label: 'Produire le tableau d\'une situation',
        formatsList: [
            { key: 'tableau_construire_trou_drag', label: 'Construire le tableau' },
            { key: 'tableau_entetes_qcm_unique', label: 'QCM' },
            { key: 'tableau_entetes_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'graphique',
        label: 'Produire le graphique d\'une situation',
        formatsList: [
            { key: 'graphique_couples_texte_trou', label: 'Les couples à placer' },
            { key: 'graphique_placer_2d', label: 'Placer dans le repère' },
            { key: 'graphique_droite_placer_2d', label: 'Placer les points et la droite' },
            { key: 'graphique_conforme_qcm_unique', label: 'QCM' },
            { key: 'graphique_lecture_tableau_vf', label: 'Vrai/Faux' },
            { key: 'graphique_graduation_qcm_unique', label: 'Graduations (QCM)' },
            { key: 'graphique_graduation_texte', label: 'Graduation (saisie)' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
